import React, { useCallback, useMemo } from "react";
import moment from "moment-timezone";
import { Eye as EyeIcon } from "lucide-react";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable";
import type {
  ClusteredTableRow,
  RawTableRow,
} from "@/lib/utils/marketSimilarity/types";
import type { VisibilityState } from "@tanstack/react-table";
import { dateWithFormatSort } from "@/components/ui/DataTable/sorting";
import { CommonDestinationTimezone } from "@/lib/utils";

interface MarketStatuses {
  ts2vec: {
    status: boolean;
    missingDates: string[];
    chosen_date: string;
    original_date: string;
    usedAdjacentDate: boolean;
  };
}

// Helper function to format rally amount values
const formatRallyAmount = (value: number | null): string => {
  if (value === null) return "-";
  return value.toFixed(2);
};

type Props = {
  statuses: MarketStatuses;
  rows: RawTableRow[] | ClusteredTableRow[];
  loading: boolean;
  isClustered: boolean;
  defaultColumnVisibility: VisibilityState;
  onDateClick?: (dateString: string) => void;
  clusterBy: 'euclidean' | 'correl' | undefined | null
};

export function ResultsTable({
  clusterBy,
  statuses,
  rows,
  loading,
  isClustered,
  defaultColumnVisibility,
  onDateClick,
}: Props) {
  const columns = useMemo(() => {
    const cols: DataTableColumn<RawTableRow | ClusteredTableRow>[] = [
    ];

    if (isClustered) {
      cols.push(
        {
          id: "view",
          header: "View",
          cell: (info) => (
            <EyeIcon className="h-4 w-4" aria-hidden="true" />
          ),
        },
      );
    }


    cols.push({
        id: "date",
        accessorKey: "date",
        sortingFn: dateWithFormatSort("MM/DD/YYYY"),
        columnFilterType: 'date',
        filterDateTimezone: CommonDestinationTimezone,
        filterDisplayFn: (v) => moment.tz(v as string, "MM/DD/YYYY",CommonDestinationTimezone).format("YYYY/MM/DD"),
        filterParseDateFn: (v) => moment.tz(v as string, "MM/DD/YYYY", CommonDestinationTimezone),
        header: "Date",
        cell: (info) => info.getValue() as string,
    });

    if (isClustered) {
      cols.push(
        {
          id: "startDate",
          sortingFn: dateWithFormatSort("MM/DD/YYYY"),
          columnFilterType: 'date',
          filterDateTimezone: CommonDestinationTimezone,
          filterDisplayFn: (v) => moment.tz(v as string, "MM/DD/YYYY",CommonDestinationTimezone).format("YYYY/MM/DD"),
          filterParseDateFn: (v) => moment.tz(v as string, "MM/DD/YYYY", CommonDestinationTimezone),
          accessorKey: "startDate",
          header: "startDate",
          cell: (info) => info.getValue() as string,
        },
        {
          id: "endDate",
          sortingFn: dateWithFormatSort("MM/DD/YYYY"),
          columnFilterType: 'date',
          filterDateTimezone: CommonDestinationTimezone,
          filterDisplayFn: (v) => moment.tz(v as string, "MM/DD/YYYY",CommonDestinationTimezone).format("YYYY/MM/DD"),
          filterParseDateFn: (v) => moment.tz(v as string, "MM/DD/YYYY", CommonDestinationTimezone),
          accessorKey: "endDate",
          header: "endDate",
          cell: (info) => info.getValue() as string,
        }
      );
    }

    cols.push(
      {
        id: "euclideanDistance",
        accessorKey: "normalizedEuclideanDistance",
        header: "Euclidean Distance",
        cell: (info) => (info.getValue() as number).toFixed(3),
      },
      {
        id: "cosineDistance",
        accessorKey: "normalizedCosineDistance",
        header: "Cosine Distance",
        cell: (info) => (info.getValue() as number).toFixed(3),
      },
      {
        id: "euclideanRank",
        accessorKey: "euclideanRank",
        header: "Euclidean Rank",
        cell: (info) => info.getValue() as number,
      },
      {
        id: "cosineRank",
        accessorKey: "cosineRank",
        header: "Cosine Rank",
        cell: (info) => info.getValue() as number,
      },
      {
        id: "avgCorrel",
        accessorKey: "meanLighthouseCorrelScore",
        header: "Avg Correl",
        cell: (info) => info.getValue() as number,
      },
      {
        id: "ts2vec",
        accessorKey: "ts2vec",
        header: "Ts2Vec",
        cell: (info) =>
          statuses?.ts2vec?.status ? (info.getValue() as number) : "--",
      },
      {
        id: "close",
        accessorKey: "close",
        header: "Close",
        cell: (info) => info.getValue() as number,
      },
      {
        id: "contAdjustedClose",
        accessorKey: "contAdjustedClose",
        header: "Cont Adjusted Close",
        cell: (info) => info.getValue() as number,
      },
      {
        id: "stxUseScore",
        accessorKey: "stxUseScore",
        header: "Stx Use Score",
        cell: (info) => info.getValue() as number,
      },
      {
        id: "seasonalScoreV1",
        accessorKey: "seasonalScoreV1",
        header: "Seasonal Score V1",
        cell: (info) => info.getValue() as number,
      },
      {
        id: "seasonalScoreV2",
        accessorKey: "seasonalScoreV2",
        header: "Seasonal Score V2",
        cell: (info) => info.getValue() as number,
      },
      {
        id: "mmScore",
        accessorKey: "mmScore",
        header: "mm Score",
        cell: (info) => info.getValue() as number,
      },
      {
        id: "algoScoreV3",
        accessorKey: "algoScoreV3",
        header: "Algo Score V3",
        cell: (info) => info.getValue() as number,
      },
      {
        id: "rsi",
        accessorKey: "rsi",
        header: "Rsi",
        cell: (info) => info.getValue() as number,
      },
      {
        id: "rally10d",
        accessorKey: "rallyAmount10d",
        header: "Rally 10d",
        cell: (info) => formatRallyAmount(info.getValue() as number | null),
      },
      {
        id: "rally15d",
        accessorKey: "rallyAmount15d",
        header: "Rally 15d",
        cell: (info) => formatRallyAmount(info.getValue() as number | null),
      },
      {
        id: "rally20d",
        accessorKey: "rallyAmount20d",
        header: "Rally 20d",
        cell: (info) => formatRallyAmount(info.getValue() as number | null),
      }
    );

    return cols;
  }, [isClustered, statuses]);

  const handleRowClick = useCallback(
    (row: RawTableRow | ClusteredTableRow, _columnId: string) => {
      if (onDateClick && isClustered) {
        onDateClick((row as ClusteredTableRow).date);
      }
    },
    [onDateClick, isClustered]
  );

  return (
    <div className="flex flex-col gap-4">
      <DataTable
        columns={columns}
        data={rows}
        enableColumnPinning
        enableColumnResizing
        enableExport
        enableMultiSelect
        enableSorting
        enableMultiSort
        enableFiltering
        enableColumnVisibility
        defaultColumnVisibility={defaultColumnVisibility}
        enableVirtualization
        virtualizationRowHeight={45}
        virtualizationOverscan={2}
        filterStyle="select"
        filterMode="internal"
        stickyHeaders
        tableId={[isClustered ? "Clustered" : "", "MarketSimPage"].join("")}
        defaultSorting={[clusterBy === 'correl' ? ({ id: "avgCorrel", desc: true }) : clusterBy === 'euclidean' ? ({ id: "euclideanDistance", desc: false }) : undefined!].filter(Boolean)}
        persistColumnSettings={true}
        loading={loading}
        emptyMessage='No data yet. Adjust inputs and click "Fetch Similarity Data".'
        tableClassName="max-h-[680px] overflow-y-scroll"
        rowClickable={isClustered && !!onDateClick}
        onRowClick={isClustered ? handleRowClick : undefined}
      />
    </div>
  );
}
